import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import config from "../../../config";
import { getCurrentOrgId } from "../../../utils/currentOrg";
import { apiGet } from "../crm/contactsKit";
import { PageLoader } from "../PageLoader";

/**
 * Shared bits for Settings → Integrations (design: screens/settings/Integrations.tsx,
 * IntegrationDetail.tsx, IntegrationConnect.tsx), mapped onto the backend's
 * integration_configs rows (GET /api/integrations, GET /api/integrations/:id).
 */

/** The five lead sources, in the design's order. `type` is the backend integration_type; `name` is the config_name the old page created rows with. */
export const SOURCES = [
  { type: "meta_leads", label: "Meta", name: "Meta (Facebook + Instagram)", description: "Capture leads from Facebook and Instagram Lead Ads", monogram: "ME" },
  { type: "google_ads", label: "Google Ads", name: "Google Ads", description: "Capture leads from Google Ads lead form assets", monogram: "GA" },
  { type: "whatsapp", label: "WhatsApp Business", name: "WhatsApp Business", description: "Capture leads from Click-to-WhatsApp ads", monogram: "WA" },
  { type: "website_form", label: "Website Form", name: "Website Form", description: "Connect forms on your website", monogram: "WF" },
  { type: "custom_webhook", label: "Custom Webhook", name: "Custom Webhook", description: "Send leads from any system to Olli", monogram: "WH" },
];

/** Legacy rows (meta_facebook / meta_instagram, google_business) belong to the Meta and Google cards, as on the old page. */
export const sourceType = (type) => (type === "meta_facebook" || type === "meta_instagram" ? "meta_leads" : type === "google_business" ? "google_ads" : type);
export const sourceOf = (type) => SOURCES.find((s) => s.type === sourceType(type)) || { type, label: type, name: type, description: "", monogram: String(type || "").slice(0, 2).toUpperCase() };
export const sourceTitle = (s) => (s.type === "meta_leads" ? "Meta (Facebook + Instagram)" : s.label);
export const isMeta = (type) => sourceType(type) === "meta_leads";
export const isWebhookType = (type) => type === "website_form" || type === "custom_webhook";

/** Where "connect" lives for each type (old IntegrationsList routing). */
export function setupRoute(row) {
  const t = sourceType(row.integration_type);
  if (t === "meta_leads") return `/settings/integrations/${row.id}/connect-meta`;
  if (t === "google_ads") return `/settings/integrations/${row.id}/connect-google`;
  if (t === "whatsapp") return `/settings/integrations/${row.id}/connect-whatsapp`;
  if (isWebhookType(t)) return `/settings/integrations/${row.id}/webhook-setup`;
  return null;
}

/** Same rules as the old IntegrationStatusBadge.computeIntegrationStatus. */
export function computeStatus(row) {
  if (!row) return "unknown";
  if (row.is_active === false || row.is_active === 0) return "inactive";
  const connected = !!row.is_connected;
  if (!connected && !row.page_id) return "not_setup";
  if (!connected && row.last_error_message) return "reconnect_required";
  if (!connected) return "disconnected";
  return "live";
}
/** Design's status word for a row: Active / Paused, plus the real states the design has no word for. */
export function statusWord(row) {
  const s = computeStatus(row);
  if (s === "inactive") return "Paused";
  if (s === "reconnect_required") return "Reconnect required";
  if (s === "not_setup" || s === "disconnected") return "Not connected";
  return "Active";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "22 Oct 2026, 6:10 AM" — the design's date format. */
export function fmtStamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const h = d.getHours();
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}
export const fmtDay = (value) => { if (!value) return ""; const d = new Date(value); return Number.isNaN(d.getTime()) ? "" : `${d.getDate()} ${MONTHS[d.getMonth()]}`; };

/** Webhook URLs, built exactly as the old pages build them. null when the signed-in user's org is unknown. */
export const customWebhookUrl = (id) => { const org = getCurrentOrgId(); return org ? `${config.API_BASE_URL}/api/webhooks/custom/${org}/${id}` : null; };
export const googleWebhookUrl = (id) => { const org = getCurrentOrgId(); return org ? `${config.API_BASE_URL}/api/webhooks/google/${org}/${id}` : null; };
export const NO_URL = "Sign in again to see your webhook URL";

/** GET /api/integrations/:id → { row, loading, reload }. */
export function useIntegration(id) {
  // { id, row }: the integration id the row on screen was loaded for. Another id
  // shows the loader (never the previous integration); reload() refreshes in place.
  const [state, setState] = useState({ id: null, row: null });
  const current = useRef(id);
  current.current = id;
  const reload = useCallback(async () => {
    const r = await apiGet(`/api/integrations/${id}`);
    const data = r?.success ? r.data : null;
    if (current.current === id) setState({ id, row: data }); // drop a late answer for an earlier id
    return data;
  }, [id]);
  useEffect(() => { reload(); }, [reload]);
  const loading = state.id !== id;
  return { row: loading ? null : state.row, loading, reload };
}

/** Design's connect screen frame: back link · centred card with monogram, title and description. */
export function ConnectFrame({ monogram, title, description, children }) {
  return <main className="mx-auto flex min-h-full max-w-5xl flex-col px-6 py-8 md:px-10">
    <Link to="/settings/integrations" className="flex w-fit items-center gap-2 rounded-full px-3 py-2 text-small font-medium text-muted hover:bg-paper hover:text-ink"><ArrowLeft size={16} strokeWidth={1.75} />Back to integrations</Link>
    <section className="mx-auto mt-5 w-full max-w-2xl rounded-lg bg-paper p-6 shadow-soft md:p-9">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-sm bg-mist text-body font-semibold">{monogram}</div>
      <h1 className="mt-4 text-center text-h3 font-semibold">{title}</h1>
      <p className="mx-auto mt-2 max-w-lg text-center text-small text-muted">{description}</p>
      {children}
    </section>
  </main>;
}

/** Shown while the integration loads, and when it does not exist. */
export function ConnectGate({ loading, row, children }) {
  if (loading) return <PageLoader />;
  if (!row) return <main className="mx-auto max-w-3xl px-8 py-12"><Link className="text-small font-semibold text-sky-deep" to="/settings/integrations">Back to integrations</Link><h1 className="mt-8 text-h2 font-semibold">This integration type is not available</h1></main>;
  return children;
}

export function CodeBlock({ children }) { return <pre className="mt-4 overflow-x-auto rounded-md bg-ink p-4 font-mono text-caption leading-6 text-paper"><code>{children}</code></pre>; }

/** Error line in the design's warning tone (the design has no error slot on these screens). */
export function ErrorNote({ children }) { return children ? <p role="alert" className="mt-4 rounded-md bg-warning-soft p-4 text-small leading-relaxed text-warning">{children}</p> : null; }

/** Design sample payload for webhook sources (documentation of accepted field names). */
export const SAMPLE_PAYLOAD = `{\n  "name": "Aarti Joshi",\n  "phone": "+919876543210",\n  "email": "aarti@example.com",\n  "message": "Interested in aligners",\n  "source": "website"\n}`;
export const htmlFormSnippet = (endpoint) => `<form action="${endpoint}" method="post">\n  <input name="name" />\n  <input name="phone" />\n  <input name="email" />\n  <button>Send</button>\n</form>`;
export const fetchSnippet = (endpoint) => `fetch("${endpoint}", {\n  method: "POST",\n  headers: { "Content-Type": "application/json" },\n  body: JSON.stringify({ name, phone, email, message })\n});`;
